import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

/**
 * The button.
 *
 * `secondary` deliberately fills with `accent-deep` rather than the logo blue:
 * white on #1a92c0 is ~3.5:1, which fails AA for small text. The logo blue is
 * still the brand colour, it just is not a text-on-fill colour. Do not "fix"
 * this by swapping it back.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-sans font-semibold rounded-md',
    'transition-colors outline-none',
    'focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
    'disabled:opacity-50 disabled:pointer-events-none',
    '[&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-brand text-white hover:bg-brand-deep',
        secondary: 'bg-accent-deep text-white hover:bg-info',
        outline: 'border border-line-strong bg-surface text-ink hover:bg-surface-sunk',
        ghost: 'text-ink hover:bg-surface-sunk',
        destructive: 'bg-danger text-white hover:brightness-90',
        'destructive-outline': 'border border-danger/40 text-danger hover:bg-danger-bg',
        link: 'text-accent-deep underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-sm [&_svg]:size-4',
        md: 'h-10 px-4 text-base [&_svg]:size-[18px]',
        lg: 'h-12 px-5 text-base [&_svg]:size-5',
        icon: 'size-10 [&_svg]:size-[18px]',
        'icon-sm': 'size-8 [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'default', size: 'md' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as the child element (e.g. a router link) instead of <button>. */
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        // A bare <button> inside a form defaults to type="submit". Defaulting
        // to "button" means only a deliberate type="submit" submits, which is
        // what every caller already assumes.
        type={asChild ? undefined : (type ?? 'button')}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
